import {
  assignDriver,
  employeeLoactionById,
  employeesLoaction,
  fetchDriverByvehicleId,
  fetchDriverDocument,
  fetchDriversLoactionByDispatchId,
  fetchLangTitles,
  fetchLocals,
  fetchUserByMobile,
  sendOtp,
  updateLanguageLocale,
  vehicleAssignToEmployee,
} from "@/ServerActions";
import { getDriverLocationETAByDispatchId } from "@/ServerActions/dispatch";
import { fetchAllUnassignedVehicle } from "@/ServerActions/vehicle";
import {
  ApiResponse,
  DocumentRes,
  DriverLocations,
  JsonRecord,
  VehicleAssignToEmployee,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const useFetchTitles = (lang: string) => {
  return useQuery<any>({
    queryKey: ["lang-titles", "DotAdmin", lang],
    queryFn: () => fetchLangTitles(lang),
    enabled: Boolean(lang),
  });
};

export const useSendOtp = () => {
  return useMutation({
    mutationFn: sendOtp,
    onError: (error) => {
      console.log(error);
    },
  });
};

export const useFetchLocals = () => {
  return useQuery<any>({
    queryKey: ["locals", "DotAdmin"],
    queryFn: () => fetchLocals(),
  });
};

export const useDriverDocuments = (driverId: string) => {
  return useQuery<ApiResponse<DocumentRes[]>>({
    queryKey: ["driver-documents", "DotAdmin", driverId],
    queryFn: () => fetchDriverDocument(driverId),
    enabled: Boolean(driverId),
  });
};

export const useFetchEmployeesLoaction = (
  arr: string[],
  serviceId?: string
) => {
  return useQuery({
    queryKey: ["employee-locations", "DotAdmin", serviceId, arr],
    queryFn: () => employeesLoaction(arr, serviceId),
    enabled: Boolean(arr.length && serviceId),
  });
};

export const useFetchDriverLocation = (
  dispatch_id: string,
  assignedDriverId: string,
  currentStatus: string
) => {
  const isPickedUp = currentStatus === "picked_up";

  return useQuery<ApiResponse<DriverLocations>>({
    queryKey: ["driver-location", "DotAdmin", dispatch_id],
    queryFn: () => fetchDriversLoactionByDispatchId(dispatch_id),

    // ✅ ONLY call when picked_up
    enabled: Boolean(dispatch_id && assignedDriverId),

    // ✅ polling logic
    refetchInterval: (query) => {
      const status = currentStatus;
      // ❌ stop completely if delivered
      if (status === "delivered") return false;
      // ✅ only poll when picked_up
      if (status === "picked_up") return 10000;
      // ❌ otherwise no polling
      return false;
    },
  });
};

export const useFetchSingleEmployeeLocation = (id: string, serviceId?: string) => {
  return useQuery<any>({
    queryKey: ["single-employee-location", "DotAdmin", id, serviceId],
    queryFn: () => employeeLoactionById(id, serviceId),
    enabled: Boolean(id),
  });
};

export const useDriverByVehicleId = (id: string, serviceId: string) => {
  return useQuery({
    queryKey: ["driver-by-vehicle", "DotAdmin", id, serviceId],
    queryFn: () => fetchDriverByvehicleId(id, serviceId),
    enabled: Boolean(id),
  });
};

export const useVehicleOfEmployee = (vendor_id: string, user_id: string, serviceId: string) => {
  return useQuery<ApiResponse<VehicleAssignToEmployee>>({
    queryKey: [
      "vehicle-by-employee",
      "DotAdmin",
      vendor_id,
      user_id,
      serviceId,
    ],
    queryFn: () => vehicleAssignToEmployee(vendor_id, user_id, serviceId),
    enabled: Boolean(user_id),
  });
};

export const useUnassignedVehciles = (fleetID: any, serviceId: string) => {
  return useQuery<ApiResponse<any>>({
    queryKey: ["unassigned-vehicles", "DotAdmin", fleetID, serviceId],
    queryFn: () => fetchAllUnassignedVehicle(fleetID, serviceId),
    enabled: Boolean(fleetID),
  });
};

type AssignDriverParams = {
  data: JsonRecord;
  serviceId: string;
  actionTypeEmployeeGet?: boolean; // ✅ NEW FLAG
};

export const useAssignDriver = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      serviceId,
    }: AssignDriverParams) => assignDriver(data, serviceId),

    onSuccess: (_data, variables) => {
      const vehicleId = variables?.data?.vehicle_id;
      const vendorId = variables?.data?.fleet_id;
      const userId = variables?.data?.driver_id;

      const isRemove = !vehicleId; // ✅ KEY LOGIC
      const actionTypeEmployeeGet = variables?.actionTypeEmployeeGet || false; // ✅ FLAG CONTROL

      // 🔥 REMOVE CASE
      if (isRemove) {

        // 1. Clear vehicle-by-employee
        queryClient.setQueryData(
          ["vehicle-by-employee", "DotAdmin", vendorId, userId, variables.serviceId],
          { data: { data: {} } }
        );

        // 2. Clear driver location (IMPORTANT FOR MAP)
        queryClient.setQueryData(
          ["single-employee-location", "DotAdmin", userId, variables.serviceId],
          { data: { data: { features: [] } } }
        );

        // 3. Invalidate employee details
        if (actionTypeEmployeeGet) {
          queryClient.removeQueries({
            queryKey: ["employee-details"],
          });
          queryClient.removeQueries({
            queryKey: ["driver-by-vehicle"],
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: ["employee-details", "DotAdmin", vendorId, userId, variables.serviceId],
          });
        }
        return; // ❗ STOP HERE (NO REFETCH)
      }

      // ✅ ASSIGN CASE (your existing logic)
      queryClient.invalidateQueries({
        queryKey: ["driver-by-vehicle", "DotAdmin", vehicleId, variables.serviceId],
      });

      queryClient.invalidateQueries({
        queryKey: [
          "vehicle-by-employee",
          "DotAdmin",
          vendorId,
          userId,
          variables.serviceId,
        ],
      });

      queryClient.invalidateQueries({
        queryKey: ["employee-details", "DotAdmin", vendorId, userId, variables.serviceId],
      });

      queryClient.invalidateQueries({
        queryKey: ["single-employee-location", "DotAdmin", userId, variables.serviceId],
      });
    },
  });
};

export const useFetchUserByMobile = () => {
  return useMutation({
    mutationFn: ({ data, serviceId }: { data: any; serviceId?: string }) =>
      fetchUserByMobile(data, serviceId),
    onError: (error) => {
      console.log(error);
    },
  });
};

export const useDriverETA = (
  dispatch_id: string,
  assignedDriverId: string
) => {
  return useQuery({
    queryKey: ["driver-eta", dispatch_id, assignedDriverId],

    queryFn: () => getDriverLocationETAByDispatchId(dispatch_id),

    // ✅ only call when driver assigned
    enabled: Boolean(dispatch_id && assignedDriverId),

    refetchInterval: (query) => {
      const data = query.state.data?.data?.data;

      const pickupEta = data?.pickup_eta;
      const deliveryEta = data?.delivery_eta;

      // ❌ stop polling if both null
      if (!pickupEta && !deliveryEta) return false;

      // ✅ otherwise every 30 sec
      return 30000;
    },
  });
};